import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoading: Promise<FFmpeg> | null = null;

function isMp4Blob(blob: Blob): boolean {
  return /^video\/mp4/i.test(blob.type);
}

function inputNameForBlob(blob: Blob): string {
  const name = blob instanceof File ? blob.name : '';
  const extMatch = name.match(/\.[a-z0-9]+$/i);
  if (extMatch) {
    return `input${extMatch[0].toLowerCase()}`;
  }
  if (/quicktime/i.test(blob.type)) {
    return 'input.mov';
  }
  if (/webm/i.test(blob.type)) {
    return 'input.webm';
  }
  if (/mp4|m4v/i.test(blob.type)) {
    return 'input.mp4';
  }
  return 'input.mov';
}

function withTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      value => {
        window.clearTimeout(timer);
        resolve(value);
      },
      error => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function waitForEvent(target: EventTarget, eventName: string, timeoutMs: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      reject(new Error(`Timed out waiting for ${eventName}`));
    }, timeoutMs);
    const onEvent = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`${eventName} failed`));
    };
    const cleanup = () => {
      window.clearTimeout(timer);
      target.removeEventListener(eventName, onEvent);
      target.removeEventListener('error', onError);
    };
    target.addEventListener(eventName, onEvent, { once: true });
    target.addEventListener('error', onError, { once: true });
  });
}

async function getFfmpeg(): Promise<FFmpeg> {
  if (ffmpegInstance?.loaded) {
    return ffmpegInstance;
  }
  if (ffmpegLoading) {
    return ffmpegLoading;
  }

  ffmpegLoading = (async () => {
    const ffmpeg = new FFmpeg();
    const base = `${window.location.origin}/assets/ffmpeg`;
    // Load from same-origin URLs. toBlobURL copies the ~30MB wasm into memory and
    // commonly hangs iOS Safari on ffmpeg.load().
    await withTimeout(
      ffmpeg.load({
        coreURL: `${base}/ffmpeg-core.js`,
        wasmURL: `${base}/ffmpeg-core.wasm`,
      }),
      20000,
      'Timed out loading converter'
    );
    ffmpegInstance = ffmpeg;
    return ffmpeg;
  })();

  try {
    return await ffmpegLoading;
  } catch (error) {
    ffmpegLoading = null;
    throw error;
  }
}

/**
 * Ensure a recorded blob is seekable MP4 (H.264 + faststart).
 * Chrome MediaRecorder usually yields WebM; Safari may already give MP4.
 */
export async function ensureMp4Blob(input: Blob): Promise<Blob> {
  if (isMp4Blob(input)) {
    return input.type === 'video/mp4' ? input : new Blob([input], { type: 'video/mp4' });
  }

  return transcodeToMp4(input, 'input.webm');
}

export type ConvertProgress = {
  stage: 'loading' | 'preparing' | 'converting' | 'finalizing';
  /** 0-1 while converting; 0 otherwise. */
  progress: number;
};

/**
 * Convert a camera-roll file (often iPhone HEVC .MOV) to seekable H.264 MP4.
 * Prefers hardware re-encode on the device; ffmpeg.wasm is a desktop fallback only.
 */
export async function convertUploadToMp4(
  input: Blob,
  onProgress?: (status: ConvertProgress) => void
): Promise<Blob> {
  onProgress?.({ stage: 'preparing', progress: 0 });
  try {
    return await reencodeViaCanvasCapture(input, onProgress);
  } catch (error) {
    console.warn('Hardware convert failed, trying ffmpeg', error);
    return transcodeToMp4(input, inputNameForBlob(input), onProgress);
  }
}

async function reencodeViaCanvasCapture(
  input: Blob,
  onProgress?: (status: ConvertProgress) => void
): Promise<Blob> {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder is not available');
  }

  const mimeType = pickRecorderMimeType();
  const url = URL.createObjectURL(input);
  const video = document.createElement('video');
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  if (!context) {
    URL.revokeObjectURL(url);
    throw new Error('Could not create canvas');
  }

  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
  video.preload = 'auto';
  video.controls = false;
  video.style.position = 'fixed';
  video.style.left = '0';
  video.style.bottom = '0';
  video.style.width = '2px';
  video.style.height = '2px';
  video.style.opacity = '0.01';
  video.style.pointerEvents = 'none';
  document.body.appendChild(video);
  video.src = url;

  let drawFrameId = 0;
  let stopped = false;

  try {
    const playAttempt = video.play();
    await waitForEvent(video, 'loadeddata', 20000);
    if (playAttempt) {
      await playAttempt.catch(() => undefined);
    }
    if (!video.videoWidth || !video.videoHeight) {
      throw new Error('Video has no dimensions');
    }

    const scale = Math.min(1, 1920 / video.videoWidth, 1080 / video.videoHeight);
    canvas.width = Math.max(2, Math.round(video.videoWidth * scale / 2) * 2);
    canvas.height = Math.max(2, Math.round(video.videoHeight * scale / 2) * 2);

    const canvasStream = canvas.captureStream(30);
    const stream = new MediaStream(canvasStream.getVideoTracks());
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType, videoBitsPerSecond: 6_000_000 } : undefined);
    const chunks: Blob[] = [];

    const recorded = new Promise<Blob>((resolve, reject) => {
      recorder.ondataavailable = event => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      recorder.onerror = () => reject(new Error('MediaRecorder failed'));
      recorder.onstop = () => {
        resolve(new Blob(chunks, { type: recorder.mimeType || mimeType || 'video/mp4' }));
      };
    });

    const draw = () => {
      if (stopped) {
        return;
      }
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      drawFrameId = requestAnimationFrame(draw);
    };

    recorder.start(250);
    draw();
    video.currentTime = 0;
    await waitForEvent(video, 'seeked', 8000).catch(() => undefined);
    await video.play();

    const onTimeUpdate = () => {
      const duration = video.duration;
      if (duration > 0 && Number.isFinite(duration)) {
        onProgress?.({ stage: 'converting', progress: Math.min(0.99, video.currentTime / duration) });
      }
    };
    video.addEventListener('timeupdate', onTimeUpdate);

    const durationMs = Number.isFinite(video.duration) && video.duration > 0
      ? video.duration * 1000 + 15000
      : 180000;

    await withTimeout(
      new Promise<void>((resolve, reject) => {
        video.onended = () => resolve();
        video.onerror = () => reject(new Error('Video failed during conversion'));
      }),
      durationMs,
      'Conversion timed out'
    );

    video.removeEventListener('timeupdate', onTimeUpdate);
    stopped = true;
    cancelAnimationFrame(drawFrameId);
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    if (recorder.state !== 'inactive') {
      recorder.stop();
    }
    stream.getTracks().forEach(track => track.stop());
    canvasStream.getTracks().forEach(track => track.stop());

    const blob = await recorded;
    if (blob.size < 1000) {
      throw new Error('Converted video was empty');
    }

    onProgress?.({ stage: 'finalizing', progress: 1 });
    return ensureMp4Blob(blob);
  } finally {
    stopped = true;
    cancelAnimationFrame(drawFrameId);
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.remove();
    URL.revokeObjectURL(url);
  }
}

async function transcodeToMp4(
  input: Blob,
  inputName: string,
  onProgress?: (status: ConvertProgress) => void
): Promise<Blob> {
  onProgress?.({ stage: 'loading', progress: 0 });
  const ffmpeg = await getFfmpeg();
  const outputName = 'output.mp4';

  onProgress?.({ stage: 'preparing', progress: 0 });
  await ffmpeg.writeFile(inputName, await fetchFile(input));

  const onFfmpegProgress = ({ progress }: { progress: number }) => {
    const clamped = Number.isFinite(progress) ? Math.min(0.99, Math.max(0, progress)) : 0;
    onProgress?.({ stage: 'converting', progress: clamped });
  };
  ffmpeg.on('progress', onFfmpegProgress);

  const attempts: string[][] = [
    [
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-pix_fmt', 'yuv420p',
      '-vf', 'scale=1920:1080:force_original_aspect_ratio=decrease',
      '-movflags', '+faststart',
      '-an',
      outputName,
    ],
    [
      '-i', inputName,
      '-c:v', 'libx264',
      '-preset', 'ultrafast',
      '-pix_fmt', 'yuv420p',
      '-movflags', '+faststart',
      '-an',
      outputName,
    ],
    [
      '-i', inputName,
      '-c', 'copy',
      '-an',
      '-movflags', '+faststart',
      outputName,
    ],
  ];

  let lastCode = -1;
  try {
    for (const args of attempts) {
      await safeDelete(ffmpeg, outputName);
      lastCode = await ffmpeg.exec(args);
      if (lastCode === 0) {
        break;
      }
    }
  } finally {
    ffmpeg.off('progress', onFfmpegProgress);
  }

  if (lastCode !== 0) {
    await safeDelete(ffmpeg, inputName);
    await safeDelete(ffmpeg, outputName);
    throw new Error(`ffmpeg exited with code ${lastCode}`);
  }

  onProgress?.({ stage: 'finalizing', progress: 1 });
  const data = await ffmpeg.readFile(outputName);
  await safeDelete(ffmpeg, inputName);
  await safeDelete(ffmpeg, outputName);

  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  return new Blob([bytes], { type: 'video/mp4' });
}

async function safeDelete(ffmpeg: FFmpeg, name: string) {
  try {
    await ffmpeg.deleteFile(name);
  } catch {
    // ignore cleanup failures
  }
}

export function pickRecorderMimeType(): string {
  const candidates = [
    'video/mp4;codecs=avc1.42E01E',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find(type => MediaRecorder.isTypeSupported(type)) ?? '';
}
